import React, { useEffect } from "react";
import { ImageData, Location } from "../../data";
import {
  Amenities,
  BackButton,
  Directions,
  Divider,
  Faqs,
  FooterV2,
  GeneralInfo,
  OfferingHeader,
  OfferingIntro,
  OfferingTestimonials,
  PromoCodeBox,
} from "../components";
import {
  getMembershipOfferingLocationFromLocationId,
  PartnerOffering,
  shouldHideLocationAndCityPicker,
} from "../../data/partner_offering";
import { PartnerConfig } from "../../offering/partner_config";
import {
  useMembershipAmenitiesQuery,
  useMembershipPoliciesQuery,
  useTestimonialsQuery,
} from "../../offering/api";
import { Helmet } from "react-helmet";
import { useI18n } from "../../lib/i18n/i18n_provider";
import {
  FAQ,
  GeneralInformation,
  ImageForIllustrationOnly,
} from "../../offering/template_offering_details_base";
import { Spacer } from "../../offering/spacer";
import { Visible } from "../../offering/visible";
import { Testimonial } from "../../data/partners";
import { Hidden } from "../../offering/hidden";
import { Container } from "../../components/container";
import { ImageGalleryMobile } from "../../offering/image_gallery_mobile";
import { ImageGalleryDesktop } from "../../offering/image_gallery_desktop";
import { Mapbox, MembershipMapbox } from "../../components/mapbox";
import { MoreToExploreMembership } from "../components/more_to_explore";
import { TalkWithUsPrompt } from "../components/talk_with_us_prompt";
import { useMediaQuery, useQueryString } from "../../hooks";
import { useAnalytics } from "../../lib/analytics/analytics";
import { MembershipLocationsV2 } from "../components/membership_location_list";
import { CancellationPolicy } from "../components/cancellation_policy";
import { OfferingDetailsMembershipQueryParams } from "./template_offering_details_membership_v2";

interface OfferingDetailsBaseProps {
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  checkoutBoxDesktop: React.ReactNode;
  checkoutBoxMobile?: React.ReactNode;
  onPressBack?: () => void;
  partnerConfig: PartnerConfig;
  spacePartnerOrGeoMarket?: boolean;
  data: {
    title?: string | React.ReactNode;
    pageTitle?: string;
    /** @deprecated Use `title` and `pageTitle` */
    offeringName: string;
    location?: Location;
    description?: React.ReactNode;
    amenitiesDescription?: string;
    faqs?: FAQ[];
    testimonials?: Testimonial[];
    imagesMobile?: ImageData[];
    imagesDesktop?: ImageData[];
    imagesModal?: ImageData[];
    generalInfo?: GeneralInformation;
    locations: Location[];
  };
}

export default function MembershipOfferingDetailsBaseV2(
  props: OfferingDetailsBaseProps
) {
  const {
    partnerConfig,
    onPressBack,
    data,
    partnerOffering,
    partnerOfferingID,
    checkoutBoxDesktop,
    checkoutBoxMobile,
    spacePartnerOrGeoMarket,
  } = props;
  const {
    imageForIllustrationOnly,
    favicon,
    showTalkWithUsPrompt = true,
    talkWithUsMessage,
    localeOptions,
    hubspotForm,
    showDirections = true,
    showAddress = true,
    hubspotForms,
    instagramLink,
    testimonialsTitle = "What Our Customers are Saying",
  } = partnerConfig;
  const {
    title,
    pageTitle,
    offeringName,
    location,
    imagesMobile,
    imagesDesktop,
    imagesModal,
    locations,
  } = data;

  const queryParams = useQueryString<OfferingDetailsMembershipQueryParams>();
  const { fbt, locale } = useI18n();

  const offeringLocation = getMembershipOfferingLocationFromLocationId(
    partnerOffering.offeringLocations,
    location?.id
  );
  const { data: amenities } = useMembershipAmenitiesQuery(offeringLocation?.id);

  const { data: membershipPolicy } = useMembershipPoliciesQuery(
    offeringLocation?.id
  );

  const shouldUseOffering =
    !location &&
    !shouldHideLocationAndCityPicker(partnerOffering.offeringLocations);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0 });
    }, 0);
  }, []);

  const generalInfo = shouldUseOffering
    ? partnerOffering.generalInfo
    : offeringLocation?.generalInfo || partnerOffering.generalInfo;
  const cancellationPolicy = membershipPolicy?.cancellationText;
  const faqs = shouldUseOffering
    ? partnerOffering.faqs
    : offeringLocation?.faqs || partnerOffering.faqs;

  const { data: partnerOfferingTestimonials } = useTestimonialsQuery(
    partnerOffering.id
  );
  const mq = useMediaQuery();
  const analytics = useAnalytics();

  return (
    <>
      <Helmet>
        <title>
          {pageTitle ||
            (typeof title === "string"
              ? title
              : `${offeringName} - ${partnerOffering.partner.name}`)}{" "}
        </title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      {/* NAVBAR */}
      <OfferingHeader
        logo={partnerOffering.partner.logoUrl}
        homepageURL={partnerOffering.partner.homepageUrl}
        offeringID={partnerOffering.id}
        partnerID={partnerOffering.partner.id}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
        talkWithUsMessage={talkWithUsMessage}
        hubspotForm={hubspotForm}
        localeOptions={localeOptions}
        hubspotForms={hubspotForms}
      />
      <div className="container">
        {/* BACK BUTTON */}
        {onPressBack && (
          <div className="py-3">
            <BackButton
              label={fbt(
                queryParams.fromCategory
                  ? "backToAllMemberships"
                  : "backToAllOffering"
              )}
              onPress={() => {
                onPressBack();

                analytics.event(
                  "Breadcrumb Click",
                  {
                    Source: "WL",
                  },
                  { onlyProviders: ["mixpanel", "customerio"] }
                );
              }}
            />
          </div>
        )}

        {/* IMAGE GALLERY */}
        <div>
          <Hidden sm>
            <Container zIndex={2}>
              {imagesMobile && (
                <ImageGalleryMobile
                  images={imagesMobile}
                  name={offeringName}
                  featuredPromoRibbon={undefined}
                />
              )}
              {imageForIllustrationOnly && <ImageForIllustrationOnly />}
            </Container>
          </Hidden>
          <Visible sm>
            <div style={{ marginLeft: -16, marginRight: -16 }}>
              {imagesDesktop && (
                <ImageGalleryDesktop
                  images={imagesDesktop}
                  name={offeringName}
                  imagesModal={imagesModal}
                  featuredPromoRibbon={undefined}
                />
              )}
              {imageForIllustrationOnly && (
                <div style={{ marginLeft: 16, position: "relative" }}>
                  <ImageForIllustrationOnly />
                </div>
              )}
            </div>
          </Visible>
        </div>
        <Spacer size={24} />

        <div className="row">
          {/* DESKTOP LEFT SIDE */}
          <div className="col-12 col-md-7 col-xl-8">
            {/* Introduction */}
            <OfferingIntro
              title={title || offeringName}
              htmlDescription={partnerOffering.description}
              address={
                showAddress && (!!location || locations.length === 1)
                  ? `${
                      location?.streetAddress || locations[0].streetAddress
                    }, ${location?.city || locations[0].city}`
                  : ""
              }
            />
            <PromoCodeBox
              spaceID={undefined}
              locationID={offeringLocation?.membership?.locatable?.id}
              locale={locale}
              partnerOffering={partnerOffering}
            />
            <Divider />

            {/* General info */}
            {!!generalInfo && (
              <>
                <GeneralInfo html={generalInfo} />
                <Divider />
              </>
            )}

            {/* Amenities */}
            {amenities?.length > 0 && (
              <>
                <Amenities data={amenities} />
                <Divider />
              </>
            )}
            {!!cancellationPolicy && (
              <CancellationPolicy html={cancellationPolicy} />
            )}
            {/* FAQs */}
            {!!faqs && <Faqs html={faqs} />}
            {(!!generalInfo || !!faqs) && <Divider />}
            {/* Direction */}
            {((!!showDirections && location) || locations?.length === 1) && (
              <Directions location={location || locations[0]} />
            )}
            {!location && spacePartnerOrGeoMarket && locations?.length > 1 && (
              <MembershipLocationsV2 locations={locations} />
            )}
          </div>
          {/* DESKTOP RIGHT SIDE */}
          <div className="col-12 col-md-5 col-xl-4">
            {mq.device !== "mobile" && checkoutBoxDesktop}
          </div>
        </div>
        <Spacer size={24} />
      </div>

      {mq.device === "mobile" && checkoutBoxMobile}

      {/* Maps - The mapbox height is 364 **/}
      {(location || locations?.length === 1) && (
        <div style={{ height: 364 }} className="container-fluid px-0">
          <Mapbox
            latitude={location?.latitude || locations[0].latitude}
            longitude={location?.longitude || locations[0].longitude}
          />
        </div>
      )}

      {!location && spacePartnerOrGeoMarket && locations?.length > 1 && (
        <div style={{ height: 364 }} className="container-fluid px-0">
          <MembershipMapbox locationList={locations} />
        </div>
      )}

      {/* TESTIMONIALS */}
      {partnerOfferingTestimonials?.length > 0 && (
        <div className="container-fluid px-0 bg-info py-5 offerings__testimonials">
          <div className="container pt-3">
            <OfferingTestimonials
              testimonials={partnerOfferingTestimonials}
              title={testimonialsTitle}
            />
          </div>
        </div>
      )}

      {/* MORE TO EXPLORE */}
      <div className="container">
        <div className="py-5">
          <MoreToExploreMembership
            partnerSlug={partnerConfig.slug}
            currentPartnerOffering={partnerOfferingID}
          />
        </div>

        {/* TALK TO US MOBILE */}
        <div className="py-1 d-flex justify-content-center d-block d-md-none mt-4">
          {showTalkWithUsPrompt ? (
            <TalkWithUsPrompt
              partnerID={partnerOffering.partner.id}
              hubspotForm={hubspotForm}
              messageableID={partnerOffering.id}
              hubspotForms={hubspotForms}
            />
          ) : (
            talkWithUsMessage && `${talkWithUsMessage}`
          )}
        </div>

        {/* FOOTER */}
        <div className="py-4">
          <FooterV2 instagramLink={instagramLink} />
        </div>
      </div>
    </>
  );
}
